import React from "react";
import { X, Command, Keyboard } from "lucide-react";

/**
 * PSShortcutHelp — Stripe-style coach card listing every Production Suite
 * keyboard shortcut. Auto-opens on first visit (localStorage flag persists
 * the dismissal so we never spam returning users), and can be re-opened
 * anytime by pressing `?` or via the dedicated help button in the header.
 *
 * Layout: two columns of <kbd>key</kbd> + description pairs grouped by
 * intent (Transport / Selection / UI). Renders nothing when closed.
 */
const SHORTCUTS = [
  {
    group: "Transport",
    items: [
      ["Space", "Play / Pause master transport"],
      ["L",     "Play forward (DAW shuttle)"],
      ["K",     "Pause"],
      ["J",     "Rewind 5 seconds"],
      ["Home",  "Skip to start"],
    ],
  },
  {
    group: "Selected track",
    items: [
      ["M", "Toggle mute"],
      ["S", "Toggle solo"],
    ],
  },
  {
    group: "Selected clip",
    items: [
      ["Del",       "Delete selected clip"],
      ["Backspace", "Delete selected clip"],
    ],
  },
  {
    group: "Workspace",
    items: [
      ["Esc", "Close any modal · stop preview"],
      ["?",   "Toggle this help"],
    ],
  },
  {
    group: "Timeline zoom",
    items: [
      ["Ctrl +",  "Zoom in (denser waveform, precise trims)"],
      ["Ctrl −",  "Zoom out (see more of a long clip at once)"],
      ["Ctrl 0",  "Reset zoom to default (50 px/s)"],
    ],
  },
  {
    group: "Clip drag",
    items: [
      ["Shift-drag", "Invert snap-to-grid — snap when Free, break out when 1/4 · 1/8"],
      ["Drag edges", "Trim the clip's start (left) or end (right) — snap-aware"],
    ],
  },
  {
    group: "Track column",
    items: [
      ["Drag grip",  "Grab the leftmost color-bar on any track header to reorder"],
      ["Right-click", "Insert a track above/below or delete the row"],
    ],
  },
];

function Kbd({ children }) {
  return (
    <kbd
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 28,
        height: 24,
        padding: "0 8px",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        color: "#e8eef3",
        background: "#1a232c",
        border: "1px solid #2c3a44",
        borderBottomWidth: 2,
        borderRadius: 4,
        textTransform: "none",
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </kbd>
  );
}

export default function PSShortcutHelp({ open, onClose }) {
  if (!open) return null;

  const dismiss = () => {
    try { localStorage.setItem("bfa-ps-shortcuts-seen", "1"); } catch { /* noop */ }
    onClose?.();
  };

  return (
    <div
      onClick={dismiss}
      data-testid="ps-shortcut-help-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "linear-gradient(180deg, #0f1620 0%, #0a1219 100%)",
          border: "1px solid #1f2a35",
          borderRadius: 12,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.02)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #1f2a35",
            background: "linear-gradient(180deg, rgba(62,207,142,0.06), transparent)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32, height: 32, borderRadius: 6,
                background: "rgba(62,207,142,0.12)",
                border: "1px solid rgba(62,207,142,0.3)",
                display: "grid", placeItems: "center",
                color: "#3ecf8e",
              }}
            >
              <Keyboard size={15} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#e8eef3", lineHeight: 1.1 }}>
                Keyboard shortcuts
              </div>
              <div style={{ fontSize: 10, color: "#6e7d8a", marginTop: 2, fontFamily: "ui-monospace, monospace" }}>
                // PRODUCTION SUITE
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="ps-shortcut-help-close-btn"
            aria-label="Close keyboard shortcuts"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: "transparent",
              border: "1px solid transparent",
              color: "#6e7d8a",
              cursor: "pointer",
              display: "grid", placeItems: "center",
              transition: "all 150ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#1a232c"; e.currentTarget.style.color = "#e8eef3"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#6e7d8a"; }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px 20px 20px", display: "grid", gap: 16 }}>
          {SHORTCUTS.map((group) => (
            <div key={group.group}>
              <div
                style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "#6e7d8a",
                  marginBottom: 8,
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                {group.group}
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {group.items.map(([key, desc]) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.02)",
                    }}
                    data-testid={`ps-shortcut-row-${key.toLowerCase()}`}
                  >
                    <Kbd>{key}</Kbd>
                    <div style={{ fontSize: 12, color: "#c2cdd5" }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px 16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #1f2a35",
            background: "rgba(255,255,255,0.01)",
          }}
        >
          <div style={{ fontSize: 10, color: "#6e7d8a", display: "flex", alignItems: "center", gap: 6 }}>
            <Command size={10} />
            Press <Kbd>?</Kbd> anytime to reopen
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="ps-shortcut-help-got-it-btn"
            style={{
              padding: "8px 16px",
              fontSize: 11,
              fontWeight: 700,
              color: "#0a1219",
              background: "#3ecf8e",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              transition: "background 150ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#5ddc9f"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#3ecf8e"; }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
